import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Globals } from 'app/_globals';
import { DashboardSite } from 'app/_models/dashboard-site.model';
import { LiveSession } from 'app/_models/live-session.model';
import DateTimeUtils from 'app/_utils/datetime.utils';
import { Subscription, timer } from 'rxjs';
import {
  DashboardAllSitePendingWorkOrders,
  DashboardHealthCheckInfo,
  DashboardService,
  DashboardSessionsGroupedBySite,
  DashboardSummaryInfo,
  DashboardWorkOrderGraphDataGroupedBySite
} from './dashboard.service';
import { LiveSessionsModalComponent } from './modals/live-sessions-modal/live-sessions-modal.component';
import { WorkOrderModalComponent } from './modals/work-order-modal/work-order-modal.component';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html'
})
export class DashboardComponent implements OnInit, OnDestroy {
  public routeRole: string;
  private timedRefreshSubscription: Subscription;
  public isSiteOwnerOrDelegate: boolean;
  public adminLastLoadTime: Date;

  // General Summary
  public allSitesSummary: DashboardSummaryInfo = {
    loading: false,
    workOrderSessions: [],
    siteSessions: [],
    workers: [],
    guestCount: 0,
  }
  public healthChecksInfo: DashboardHealthCheckInfo = {
    loading: false,
    healthChecksDue: [],
    expectedCheckOffTimesDue: [],
  };
  public allSitePendingWorkOrders: DashboardAllSitePendingWorkOrders = {
    loading: false,
    workOrders: [],
  };

  // Sites
  public sites: DashboardSite[] = [];
  public filteredSites: DashboardSite[] = [];
  public filterText: string = '';

  // Individual Site's cards
  public workOrderGraphDataGroupedBySite: DashboardWorkOrderGraphDataGroupedBySite = { loading: false };
  public sessionsGroupedBySite: DashboardSessionsGroupedBySite = { loading: false };

  constructor(
    private dashboardService: DashboardService,
    private modalService: NgbModal,
    private router: Router,
    globals: Globals,
  ) {
    this.routeRole = globals.user.routeRole;
    this.isSiteOwnerOrDelegate = this.routeRole === 'site-owner' || this.routeRole === 'site-delegate';
  }

  ngOnInit() {
    this.setupSubscriptionsForAllData();

    this.fetchInfrequentlyChangingData();
    this.fetchFrequentlyChangingData();

    this.adminOnlyGetLastLoadTimeFromLocalStorage();
  }

  // Set up subscriptions listening to the Dashboard Service's 'ReplaySubjects'.
  // Triggering network requests is handled separately.
  // When the user returns to the page for a second time, the ReplaySubject will have the previous value, until the data refreshes.
  public setupSubscriptionsForAllData() {
    // sites list
    this.dashboardService.sitesObservable().subscribe(data => {
      this.sites = data;
      this.filterSites();
    });

    // all site summary
    this.dashboardService.liveSessionsObservable().subscribe(data => { this.allSitesSummary = data; });
    this.dashboardService.healthChecksDueObservable().subscribe(data => { this.healthChecksInfo = data; });
    this.dashboardService.allSitePendingWorkOrdersObservable().subscribe(data => { this.allSitePendingWorkOrders = data; });

    // each site row
    this.dashboardService.workOrderGraphDataGroupedBySiteObservable().subscribe(data => { this.workOrderGraphDataGroupedBySite = data; });
    this.dashboardService.sessionsGroupedBySiteObservable().subscribe(data => { this.sessionsGroupedBySite = data; });
  }

  // only fetch once, when user navigates to the page
  public fetchInfrequentlyChangingData() {
    if (this.isSiteOwnerOrDelegate) {
      this.dashboardService.fetchSites();
    }
  }

  // regularly refresh while user is on the page
  public fetchFrequentlyChangingData() {
    const numberOfMinutes = 1;

    this.timedRefreshSubscription = timer(0, DateTimeUtils.MILLISECONDS_IN_A_MINUTE * numberOfMinutes).subscribe(() => {
      this.allSitesSummary.loading = true;
      this.healthChecksInfo.loading = true;
      this.sessionsGroupedBySite.loading = true;
      this.dashboardService.fetchLiveSessions();

      if (this.isSiteOwnerOrDelegate) {
        this.workOrderGraphDataGroupedBySite.loading = true;
        this.allSitePendingWorkOrders.loading = true;
        this.dashboardService.fetchWorkOrders();


      }
    });
  }

  ngOnDestroy() {
    this.dashboardService.cancelSubscriptions();

    if (this.timedRefreshSubscription) {
      this.timedRefreshSubscription.unsubscribe();
    }
  }

  public goToLiveSessionsList() {
    this.navigateToLiveSessions({ tab: 'list' });
  }

  public navigateToLiveSessions(queryParams: any) {
    this.router.navigate(['/', this.routeRole, 'live-sessions'], { queryParams });
  }

  public openWorkOrdersModal(workOrders) {
    const modalRef = this.modalService.open(WorkOrderModalComponent, { centered: true, size: 'lg' });
    modalRef.componentInstance.showSite = true;
    modalRef.componentInstance.graphData = {
      label: 'open',
      value: workOrders.length,
      data: workOrders,
    };
  }

  public openWorkOrdersModalFromSessions(workOrderSessions) {
    const modalRef = this.modalService.open(WorkOrderModalComponent, { centered: true, size: 'lg' });
    modalRef.componentInstance.showSite = true;
    modalRef.componentInstance.graphData = {
      label: 'open',
      value: workOrderSessions.length,
      data: workOrderSessions.map(workOrderSession => this.workOrderMapper(workOrderSession)),
    };
  }
  private workOrderMapper(workOrderSession: any):any {
    return {
      id: workOrderSession.workOrderId,
      description: workOrderSession.description,
      lastModifiedDate: workOrderSession.workOrderLastModifiedDate,
      createdDate: workOrderSession.workOrderCreatedDate,
      assetName: workOrderSession.assetName,
      siteName: workOrderSession.site,
      siteId: workOrderSession.siteId,
    }
  }

  public openSessionsModal(sessions: LiveSession[]) {
    const modalRef = this.modalService.open(LiveSessionsModalComponent, { centered: true, size: 'lg' });
    modalRef.componentInstance.sessionData = sessions;
  }

  public onFilterTextChanged(filterText: string) {
    this.filterText = filterText;

    this.filterSites();
  }

  private filterSites() {
    // reset all by cloning non-filtered list
    this.filteredSites = this.sites.map(site => site.clone());

    if (this.filterText) {
      this.filteredSites = this.filteredSites.filter(site => {
        return (site.name.toLocaleLowerCase().includes(this.filterText.toLocaleLowerCase()));
      });
    }
  }

  public loadSitesForAdmin() {
    this.adminLastLoadTime = new Date();
    localStorage.setItem('ui.admin.dashboard.lastLoadTime', JSON.stringify(this.adminLastLoadTime));

    this.dashboardService.fetchSites();

    this.workOrderGraphDataGroupedBySite.loading = true;
    this.allSitePendingWorkOrders.loading = true;
    this.dashboardService.fetchWorkOrders();
  }

  private adminOnlyGetLastLoadTimeFromLocalStorage() {
    if (!this.isSiteOwnerOrDelegate) {
      const savedOptions = localStorage.getItem('ui.admin.dashboard.lastLoadTime');

      if (this.sites.length > 0 && savedOptions) {
        this.adminLastLoadTime = JSON.parse(savedOptions);
      }
    }
  }
}
